import React, { FunctionComponent, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { isValid as isValidCpf } from '@fnando/cpf';
import { isValid as isValidCnpj } from '@fnando/cnpj';
import { useHistory, useParams } from 'react-router-dom';

import Layout from '../../containers/Layout';
import Button from '../../components/Button';
import Title from '../../components/Title';
import Input from '../../components/Input';
import { Form } from './styles';
import { useDefaultGtm } from '../../hooks/gtm/default/useDefaultGtm';
import { useOrder } from '../../hooks/order/useOrder';
import { Order } from '../../requests/caipirinha-api/interfaces/order.interface';
import { normalizeName } from '../../utils/text.util';
import { LGPDInvite } from '../../requests/interfaces/commons.interface';
import { getLocationState } from '../../utils/routes.util';
import { addSessionIdToTMX } from '../../utils/threatmetrix';
import IcSecurity from '../../assets/icons/ic_security.svg';

interface Wrapper {
  order: Order;
  lgpd?: LGPDInvite;
}

const withDocument = (
  Wrapper: FunctionComponent<Wrapper>,
  description: string,
  isOnlyPaid = false,
) => {
  return (props: any) => {
    const cachedOrder: Order | undefined = getLocationState(props, 'order');
    const cachedLgpd: LGPDInvite | undefined = getLocationState(props, 'lgpd');
    const history = useHistory();

    const {
      order,
      lgpd,
      getOrderByIdAndDocument,
      getOrderPaidByIdAndDocument,
      loading,
      customer,
      session,
      nameLoading,
      getNameById,
    } = useOrder();

    const { id } = useParams<{ id: string }>();

    let documentName = 'CPF';
    let isCpf = true;

    if (customer && customer.legalNature === 'J') {
      documentName = 'CNPJ';
      isCpf = false;
    }

    const replacedDescription = description.replace(
      '{documentName}',
      documentName,
    );

    useEffect(() => {
      if (history.action === 'POP') {
        getNameById(id);
      }
    }, [id, history.action]);

    const { sendDataToGtm } = useDefaultGtm();

    useEffect(() => {
      sendDataToGtm(
        { type: 'informe_o_cpf', name: '/pagamento/identificacao' },
        order,
        !!order,
      );
    }, [order]);

    useEffect(() => {
      session !== null && addSessionIdToTMX(session);
    }, [session]);

    const cpfValidator = Yup.string()
      .test('document', 'CPF inválido', value => isValidCpf(value, false))
      .required('informe seu CPF para continuar');

    const cnpjValidator = Yup.string()
      .test('document', 'CNPJ inválido', value => isValidCnpj(value, false))
      .required('informe seu CNPJ para continuar');

    const {
      handleSubmit,
      setFieldValue,
      errors,
      touched,
      handleBlur,
      values: { document },
    } = useFormik({
      initialValues: {
        document: '',
      },
      validationSchema: Yup.object().shape({
        document: isCpf ? cpfValidator : cnpjValidator,
      }),
      onSubmit: async values => {
        const documentValue = values.document.replace(/[^\d]/g, '');
        if (isOnlyPaid) {
          await getOrderPaidByIdAndDocument(id, documentValue);
        } else {
          await getOrderByIdAndDocument(id, documentValue);
        }
      },
    });

    const validOrder = order || cachedOrder;
    if (validOrder) {
      return <Wrapper order={validOrder} lgpd={lgpd || cachedLgpd} />;
    }

    return (
      <Form>
        <Layout
          loading={nameLoading}
          title={`Informe o ${documentName}`}
          bottom={
            <Button data-cy='layout-button-continue' type="submit" loading={loading} onClick={handleSubmit}>
              Continuar
            </Button>
          }
        >
          <Title data-cy='description' icon={IcSecurity}>
            <strong>
              {customer ? `${normalizeName(customer.name)}, ` : ''}
            </strong>
            {replacedDescription}
          </Title>
          <Input
            placeholder={`Número de ${documentName}`}
            type="text"
            min={11}
            max={11}
            pattern="[0-9]*"
            inputMode="numeric"
            id="document"
            autoComplete="off"
            onBlur={handleBlur}
            name="document"
            touched={String(touched.document)}
            error={errors.document}
            value={document}
            onChange={({ target: { value } }) => {
              setFieldValue('document', value.replace(/[^\d]/g, ''));
            }}
          />
        </Layout>
      </Form>
    );
  };
};

export default withDocument;
